import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { Employee, DisplayGroup, EmployeeRole, NameDisplayFormat } from '../../shared/types.js';
import Button from '../components/Button';
import AddNewEmployeeModal from '../components/AddNewEmployeeModal';
import AssignEmployeeToGroupsModal from '../components/AssignEmployeeToGroupsModal';
import PINModal from '../components/PINModal';
import { cloneDeep, toDateInputString, formatEmployeeName } from '../utils.js';

const FormInput: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, ...props }) => (
    <div>
        <label htmlFor={props.id} className="block text-sm font-medium text-gray-500 dark:text-gray-400">{label}</label>
        <input {...props} className="mt-1 block w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary" />
    </div>
);

const FormSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }> = ({ label, children, ...props }) => (
    <div>
        <label htmlFor={props.id} className="block text-sm font-medium text-gray-500 dark:text-gray-400">{label}</label>
        <select {...props} className="mt-1 block w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary disabled:opacity-60 disabled:cursor-not-allowed">
            {children}
        </select>
    </div>
);

const EmployeesSettingsTab: React.FC = () => {
    const { employees, displayGroups, addEmployee, updateEmployee, deleteEmployee, overtimeRules, assignOvertimeRuleToEmployee } = useTimeClock();
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
    const [editingOvertimeRuleId, setEditingOvertimeRuleId] = useState<string | undefined>();
    const [employeeFilter, setEmployeeFilter] = useState<'all' | 'active'>('active');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [activeInnerTab, setActiveInnerTab] = useState('Settings');
    const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
    const [isPinModalOpen, setIsPinModalOpen] = useState(false);
    const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [isApplying, setIsApplying] = useState(false);
    const adminCount = useMemo(() => employees.filter(e => e.role === EmployeeRole.ADMIN).length, [employees]);
    const filteredEmployees = useMemo(() => {
        return employeeFilter === 'active' ? employees.filter(e => (e.role ?? EmployeeRole.EMPLOYEE) !== EmployeeRole.INACTIVE) : employees;
    }, [employees, employeeFilter]);
    const sortedEmployees = useMemo(() => 
        cloneDeep(filteredEmployees).sort((a,b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')), 
    [filteredEmployees]);
    useEffect(() => {
        const firstEmployeeId = sortedEmployees[0]?.id || null;
        if (!selectedEmployeeId && firstEmployeeId) {
            setSelectedEmployeeId(firstEmployeeId);
        } else if (selectedEmployeeId && !sortedEmployees.some(u => u.id === selectedEmployeeId)) {
            setSelectedEmployeeId(firstEmployeeId);
        }
    }, [sortedEmployees, selectedEmployeeId]);
    const selectedEmployee = useMemo(() => {
        return employees.find(e => e.id === selectedEmployeeId) || null;
    }, [selectedEmployeeId, JSON.stringify(employees.find(e => e.id === selectedEmployeeId))]);
    const originalOvertimeRule = useMemo(() => {
        if (!selectedEmployeeId) return undefined;
        return overtimeRules.find(r => r.assignedEmployeeIds.includes(selectedEmployeeId));
    }, [selectedEmployeeId, JSON.stringify(overtimeRules.find(r => r.assignedEmployeeIds.includes(selectedEmployeeId)))]);
    useEffect(() => {
        if (selectedEmployee) {
            setEditingEmployee(cloneDeep(selectedEmployee));
            setEditingOvertimeRuleId(originalOvertimeRule?.id);
            setSaveMessage(null);
        } else {
            setEditingEmployee(null);
            setEditingOvertimeRuleId(undefined);
            setSaveMessage(null);
        }
    }, [selectedEmployee, originalOvertimeRule]);
    const isDirty = useMemo(() => {
        if (!selectedEmployee || !editingEmployee) return false;
        const overtimeRuleChanged = originalOvertimeRule?.id !== editingOvertimeRuleId;
        const employeeObjectChanged = JSON.stringify(selectedEmployee) !== JSON.stringify(editingEmployee);
        return employeeObjectChanged || overtimeRuleChanged;
    }, [selectedEmployee, editingEmployee, originalOvertimeRule, editingOvertimeRuleId]);
    const employeeGroups = useMemo(() => {
        if (!selectedEmployeeId) return [];
        return displayGroups.filter(g => g.employeeIds.includes(selectedEmployeeId));
    }, [selectedEmployeeId, displayGroups]);
    const handleAddEmployee = () => {
        setIsAddEmployeeModalOpen(true);
    };
    const handleDeleteEmployee = () => {
        if (selectedEmployeeId && confirm(`Are you sure you want to disable kiosk access for ${selectedEmployee?.firstName} ${selectedEmployee?.lastName}? They will be removed from the time clock but will remain in OrangeHRM.`)) {
            deleteEmployee(selectedEmployeeId);
        }
    };
    const handleSaveNewEmployee = async (data: { emp_number: number, pin: string }) => {
        try {
            await addEmployee(data);
            setIsAddEmployeeModalOpen(false);
        } catch (error: any) {
            alert(error.message);
        }
    };
    const handleApply = async () => {
        if (!editingEmployee || isApplying) return;
        setIsApplying(true);
        setSaveMessage(null);
        try {
            await updateEmployee(editingEmployee);
            if (editingOvertimeRuleId !== undefined) {
                 await assignOvertimeRuleToEmployee(editingEmployee.id, editingOvertimeRuleId);
            }
            setSaveMessage({ type: 'success', text: 'Employee settings saved.' });
        } catch (error: any) {
            setSaveMessage({ type: 'error', text: error?.message || 'Failed to save employee settings.' });
        } finally {
            setIsApplying(false);
        }
    };
    const handleRevert = () => {
        if(selectedEmployee) {
            setEditingEmployee(cloneDeep(selectedEmployee));
            setEditingOvertimeRuleId(originalOvertimeRule?.id);
        }
    };
    const handleUpdateField = (field: keyof Employee, value: any) => {
        if(editingEmployee) {
            setSaveMessage(null);
            setEditingEmployee(prev => prev ? {...prev, [field]: value} : null);
        }
    }
    const handleSavePin = async (pin: string) => {
        if (editingEmployee) {
            handleUpdateField('pin', pin);
        }
        setIsPinModalOpen(false);
    };
    const handleSaveGroups = (groupIds: string[]) => {
        setIsGroupsModalOpen(false);
    };
    const isDeleteDisabled = !selectedEmployee || (adminCount <= 1 && selectedEmployee.role === EmployeeRole.ADMIN);
    const isRoleDropdownDisabled = adminCount <= 1 && editingEmployee?.role === EmployeeRole.ADMIN;
    return (
        <div className="flex bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg h-[calc(100vh-22rem)]">
            <div className="w-1/3 md:w-1/4 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex-grow p-2 space-y-1 overflow-y-auto">
                    {sortedEmployees.map(employee => (
                        <button
                            key={employee.id}
                            onClick={() => setSelectedEmployeeId(employee.id)}
                            className={`w-full text-left p-3 rounded-lg transition-colors ${selectedEmployeeId === employee.id ? 'bg-brand-secondary text-white font-bold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'} ${employee.role === EmployeeRole.INACTIVE ? 'opacity-50' : ''}`}
                        >
                            {`${employee.lastName}, ${employee.firstName}`}
                        </button>
                    ))}
                </div>
                <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                    <div className="flex gap-2 w-full">
                        <Button size="sm" onClick={handleAddEmployee} className="flex-1">Add</Button>
                        <Button 
                            size="sm" 
                            variant="danger" 
                            onClick={handleDeleteEmployee} 
                            disabled={isDeleteDisabled}
                            title={isDeleteDisabled ? "Cannot delete this user." : "Disable kiosk access"}
                            className="flex-1"
                        >
                            Delete
                        </Button>
                    </div>
				</div>
				<div className="pl-2 pr-2 pb-2 flex gap-2">
                    <div className="relative flex-1">
                        <Button
                            size="sm" variant="secondary" onClick={() => setIsFilterDropdownOpen(p => !p)}
                            className="w-full justify-between"
                        >
                            <span>{employeeFilter === 'active' ? 'Show Active' : 'Show All'}</span>
                            <svg className={`w-4 h-4 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                        </Button>
                        {isFilterDropdownOpen && (
                            <div className="absolute bottom-full left-0 mb-1 w-full bg-white dark:bg-gray-700 rounded-md shadow-lg z-20 ring-1 ring-black ring-opacity-5">
                                <ul className="py-1">
                                    <li><button onClick={() => { setEmployeeFilter('all'); setIsFilterDropdownOpen(false); }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-white hover:bg-brand-secondary hover:text-white"><span className="w-4 text-brand-secondary">{employeeFilter === 'all' && '✓'}</span>Show All</button></li>
                                    <li><button onClick={() => { setEmployeeFilter('active'); setIsFilterDropdownOpen(false); }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-white hover:bg-brand-secondary hover:text-white"><span className="w-4 text-brand-secondary">{employeeFilter === 'active' && '✓'}</span>Show Active</button></li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="w-2/3 md:w-3/4 flex flex-col">
                {editingEmployee ? (
                    <>
						<div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Employee Name</label>
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-2xl font-bold">{formatEmployeeName(editingEmployee, NameDisplayFormat.LAST_FIRST)}</p>
                                <Button
                                    variant="secondary"
                                    href={`https://hr.staff.metropei.com/web/index.php/pim/viewPersonalDetails/empNumber/${editingEmployee.emp_number}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Edit Info
                                </Button>
                            </div>
						</div>
						<div className="flex border-b border-gray-200 dark:border-gray-700">
							{['Settings', 'Notes'].map(tabName => (
								 <button key={tabName} onClick={() => setActiveInnerTab(tabName)} className={`py-2 px-4 text-sm ${activeInnerTab === tabName ? 'text-brand-secondary border-b-2 border-brand-secondary font-semibold' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>{tabName}</button>
							))}
						</div>
						<div className="flex-grow p-6 overflow-y-auto">
						   {activeInnerTab === 'Settings' && (
							   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
									<div className="space-y-4">
										<div className="p-4 border border-gray-300 dark:border-gray-700 rounded-lg"><h3 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">Password</h3><div className="flex items-center justify-between text-gray-900 dark:text-white"><span>••••</span><Button variant="secondary" size="sm" onClick={() => setIsPinModalOpen(true)}>Modify</Button></div></div>
										<FormSelect 
                                            label="Status" 
                                            id="role" 
                                            value={editingEmployee.role || EmployeeRole.EMPLOYEE} 
                                            onChange={e => handleUpdateField('role', e.target.value as EmployeeRole)}
                                            disabled={isRoleDropdownDisabled}
                                            title={isRoleDropdownDisabled ? "The last administrator cannot change their own role." : ""}
                                        >
                                            <option value={EmployeeRole.EMPLOYEE}>Employee</option>
                                            <option value={EmployeeRole.ADMIN}>Admin</option>
                                            <option value={EmployeeRole.INACTIVE}>Inactive</option>
                                        </FormSelect>
							   <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
								   <FormSelect label="Overtime" id="overtime" value={editingOvertimeRuleId || ''} onChange={e => setEditingOvertimeRuleId(e.target.value)}>
									   <option value="">No rule assigned</option>
									   {overtimeRules.map(rule => (
										   <option key={rule.id} value={rule.id}>{rule.name}</option>
									   ))}
								   </FormSelect>
								   <FormInput label="Employee #" id="employeeNumber" type="text" value={editingEmployee.employeeNumber || ''} onChange={e => handleUpdateField('employeeNumber', e.target.value)} disabled />
							   </div>
									</div>
									<div className="border border-gray-300 dark:border-gray-700 rounded-lg flex flex-col"><h3 className="p-2 text-center font-semibold bg-gray-100 dark:bg-gray-700/50">Display Group Member</h3><div className="flex-grow overflow-y-auto p-2">{employeeGroups.map(g => <div key={g.id} className="p-1 text-gray-800 dark:text-gray-200">{g.name}</div>)}{employeeGroups.length === 0 && <p className="text-gray-500 dark:text-gray-400 text-center p-4">Not a member of any group.</p>}</div><div className="p-2 border-t border-gray-200 dark:border-gray-700 text-right"><Button variant="secondary" size="sm" onClick={() => setIsGroupsModalOpen(true)}>Edit</Button></div></div>
								</div>
						   )}
							{activeInnerTab === 'Notes' && (
								<div>
								   <label htmlFor="notes" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Notes</label>
								   <textarea id="notes" value={editingEmployee.notes || ''} onChange={(e) => handleUpdateField('notes', e.target.value)} rows={10} className="w-full bg-white dark:bg-gray-700 p-2 rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-secondary"></textarea>
								</div>
							)}
						</div>
						<div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 flex justify-end gap-4">
                            {saveMessage && (
                                <div
                                    role="status"
                                    className={`mr-auto rounded-md px-3 py-2 text-sm font-medium ${
                                        saveMessage.type === 'error'
                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'
                                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200'
                                    }`}
                                >
                                    {saveMessage.text}
                                </div>
                            )}
							<Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
							<Button onClick={handleApply} disabled={!isDirty || isApplying}>{isApplying ? 'Applying...' : 'Apply'}</Button>
						</div>
                    </>
                ) : (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">
                        {employees.length > 0 ? "Select a employee to see details" : "No employees enabled. Click 'Add' to begin."}
                    </div>
                )}
            </div>
            <AddNewEmployeeModal
                isOpen={isAddEmployeeModalOpen}
                onClose={() => setIsAddEmployeeModalOpen(false)}
                onSave={handleSaveNewEmployee}
            />
            {editingEmployee && (
                <PINModal
                    isOpen={isPinModalOpen}
                    onClose={() => setIsPinModalOpen(false)}
                    onSave={handleSavePin}
                    currentPin={editingEmployee.pin}
                />
            )}
            {editingEmployee && (
                <AssignEmployeeToGroupsModal
                    isOpen={isGroupsModalOpen}
                    onClose={() => setIsGroupsModalOpen(false)}
                    onSave={handleSaveGroups}
                    employee={editingEmployee}
                />
            )}
        </div>
    );
};

export default EmployeesSettingsTab;
